import React, { useState, useEffect, useCallback } from 'react';
import { getParticipantsAvailability, findFreeTime, timeToMinutes, minutesToTime } from '../utils/conflictUtils';

/**
 * Painel de disponibilidade de participantes.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - date: string "YYYY-MM-DD"
 *  - startTime: string "HH:mm"
 *  - endTime: string "HH:mm"
 *  - participantsUids: string[]
 *  - systemUsers: [{ id, name, email }]
 *  - excludeEventId: string | null
 *  - onApplyTime: (startTime, endTime) => void   ← callback para injetar horário no formulário
 */
function AvailabilityPanel({ isOpen, onClose, date, startTime, endTime, participantsUids, systemUsers, excludeEventId, onApplyTime }) {
    const [loading, setLoading]       = useState(false);
    const [results, setResults]       = useState([]);
    const [suggesting, setSuggesting] = useState(false);
    const [suggestedTime, setSuggestedTime] = useState(null); // { start, end }
    const [appliedTime, setAppliedTime] = useState(null); // tempo aplicado para re-check

    const currentStart = appliedTime ? appliedTime.start : startTime;
    const currentEnd   = appliedTime ? appliedTime.end   : endTime;

    const runCheck = useCallback(async (checkStart, checkEnd) => {
        if (!participantsUids?.length || !date) return;
        setLoading(true);
        setSuggestedTime(null);
        try {
            const data = await getParticipantsAvailability(
                participantsUids, date, checkStart, checkEnd, systemUsers, excludeEventId
            );
            setResults(data);
        } catch (err) {
            console.error('[AvailabilityPanel] Erro ao checar disponibilidade:', err);
        } finally {
            setLoading(false);
        }
    }, [participantsUids, date, systemUsers, excludeEventId]);

    // Roda a verificação quando abre o painel
    useEffect(() => {
        if (isOpen) {
            setAppliedTime(null);
            setSuggestedTime(null);
            setResults([]);
            runCheck(startTime, endTime);
        }
    }, [isOpen]); // eslint-disable-line

    if (!isOpen) return null;

    const allAvailable   = results.length > 0 && results.every(r => r.disponivel);
    const someUnavailable = results.some(r => !r.disponivel);
    const duration        = timeToMinutes(currentEnd) - timeToMinutes(currentStart);

    const handleSuggest = async () => {
        setSuggesting(true);
        setSuggestedTime(null);
        try {
            const freeStart = await findFreeTime(date, duration > 0 ? duration : 60, participantsUids, excludeEventId);
            if (freeStart) {
                const freeEnd = minutesToTime(timeToMinutes(freeStart) + (duration > 0 ? duration : 60));
                setSuggestedTime({ start: freeStart, end: freeEnd });
                // Re-verifica com o novo horário
                setAppliedTime({ start: freeStart, end: freeEnd });
                await runCheck(freeStart, freeEnd);
            } else {
                alert('Não foi encontrado nenhum horário livre em comum entre 08:00 e 18:00 nesta data.');
            }
        } finally {
            setSuggesting(false);
        }
    };

    const handleApply = () => {
        if (suggestedTime) {
            onApplyTime(suggestedTime.start, suggestedTime.end);
        }
        onClose();
    };

    const collectionLabel = (col) => {
        const map = {
            auditorias:          'Agenda Qualidade',
            auditorias_homecare: 'Agenda Homecare',
            agendaDirecao:       'Agenda Direção',
            agenda_pessoal:      'Agenda Pessoal'
        };
        return map[col] || col;
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 4000,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px'
        }} onClick={onClose}>
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 24px 60px rgba(0,0,0,0.25)',
                width: '100%',
                maxWidth: '900px',
                maxHeight: '90vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    padding: '20px 28px',
                    borderBottom: '1px solid #e9ecef',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    background: 'linear-gradient(135deg, #00695c 0%, #00897b 100%)',
                    color: '#fff',
                    borderRadius: '16px 16px 0 0'
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                            🔍 Verificação de Agendas
                        </h3>
                        <p style={{ margin: '4px 0 0', fontSize: '0.85rem', opacity: 0.85 }}>
                            Data: {date ? date.split('-').reverse().join('/') : '—'} &nbsp;|&nbsp;
                            Horário: {currentStart} → {currentEnd}
                        </p>
                    </div>
                    <button onClick={onClose} style={{
                        background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
                        borderRadius: '50%', width: '32px', height: '32px',
                        fontSize: '1.1rem', cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', lineHeight: 1
                    }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px 0' }}>
                            <div style={{
                                width: '48px', height: '48px', margin: '0 auto 16px',
                                border: '4px solid #e9ecef',
                                borderTop: '4px solid #00897b',
                                borderRadius: '50%',
                                animation: 'spin 0.8s linear infinite'
                            }} />
                            <p style={{ color: '#6c757d', fontSize: '1rem', margin: 0 }}>
                                ⏳ Carregando disponibilidades...
                            </p>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : results.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                            Nenhum participante selecionado.
                        </p>
                    ) : (
                        <>
                            {/* Resumo */}
                            <div style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                marginBottom: '20px', padding: '12px 16px',
                                background: allAvailable ? '#d4edda' : '#fff3cd',
                                border: `1px solid ${allAvailable ? '#c3e6cb' : '#ffeeba'}`,
                                borderRadius: '8px'
                            }}>
                                <span style={{ fontWeight: 600, color: allAvailable ? '#155724' : '#856404', fontSize: '0.95rem' }}>
                                    {allAvailable
                                        ? '✅ Todos os participantes estão disponíveis!'
                                        : `⚠️ ${results.filter(r => !r.disponivel).length} participante(s) com conflito de horário`
                                    }
                                </span>
                                <span style={{ fontSize: '0.85rem', color: '#6c757d' }}>
                                    {results.filter(r => r.disponivel).length}/{results.length} disponíveis
                                </span>
                            </div>

                            {/* Cards dos participantes */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                                gap: '16px'
                            }}>
                                {results.map(r => (
                                    <div key={r.uid} style={{
                                        border: `2px solid ${r.disponivel ? '#28a745' : '#dc3545'}`,
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        background: '#fff',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                                    }}>
                                        {/* Card Header */}
                                        <div style={{
                                            background: r.disponivel ? '#28a745' : '#dc3545',
                                            padding: '12px 16px',
                                            display: 'flex', alignItems: 'center', gap: '10px'
                                        }}>
                                            <div style={{
                                                width: '36px', height: '36px', borderRadius: '50%',
                                                background: 'rgba(255,255,255,0.25)',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: 700, fontSize: '1rem', color: '#fff', flexShrink: 0
                                            }}>
                                                {r.name.charAt(0).toUpperCase()}
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontWeight: 600, color: '#fff', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {r.name}
                                                </div>
                                                <div style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.85)', marginTop: '2px' }}>
                                                    {r.disponivel ? '✅ Disponível' : '🔴 Com conflito'}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card Body */}
                                        <div style={{ padding: '12px 16px', minHeight: '80px' }}>
                                            {r.disponivel ? (
                                                <p style={{ margin: 0, color: '#28a745', fontSize: '0.85rem', fontWeight: 500 }}>
                                                    Livre no horário selecionado ✓
                                                </p>
                                            ) : (
                                                <>
                                                    <p style={{ margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 600, color: '#dc3545' }}>
                                                        Compromissos conflitantes:
                                                    </p>
                                                    {r.conflitos.map((c, i) => (
                                                        <div key={i} style={{
                                                            fontSize: '0.8rem', padding: '6px 8px', marginBottom: '4px',
                                                            background: '#fff5f5', borderRadius: '6px',
                                                            border: '1px solid #f5c6cb', color: '#721c24'
                                                        }}>
                                                            <strong>{c.horario} → {c.horarioFim}</strong>
                                                            <br />
                                                            <span style={{ opacity: 0.85 }}>{c.descricao}</span>
                                                            <br />
                                                            <span style={{ fontSize: '0.72rem', opacity: 0.65 }}>{collectionLabel(c.collection)}</span>
                                                        </div>
                                                    ))}
                                                </>
                                            )}

                                            {/* Horários livres */}
                                            {r.horariosLivres.length > 0 && (
                                                <div style={{ marginTop: '10px' }}>
                                                    <p style={{ margin: '0 0 6px', fontSize: '0.75rem', fontWeight: 600, color: '#6c757d' }}>
                                                        Disponível em:
                                                    </p>
                                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                        {r.horariosLivres.slice(0, 6).map((h, i) => (
                                                            <span key={i} style={{
                                                                fontSize: '0.72rem', padding: '3px 7px',
                                                                background: '#e8f5e9', borderRadius: '10px',
                                                                color: '#2e7d32', border: '1px solid #c8e6c9',
                                                                fontWeight: 500
                                                            }}>
                                                                {h.inicio}–{h.fim}
                                                            </span>
                                                        ))}
                                                        {r.horariosLivres.length > 6 && (
                                                            <span style={{ fontSize: '0.72rem', color: '#6c757d', padding: '3px 4px' }}>
                                                                +{r.horariosLivres.length - 6}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Sugestão aplicada */}
                            {suggestedTime && (
                                <div style={{
                                    marginTop: '20px', padding: '14px 18px',
                                    background: '#e0f2f1', border: '1px solid #80cbc4',
                                    borderRadius: '10px', display: 'flex', alignItems: 'center', gap: '12px'
                                }}>
                                    <span style={{ fontSize: '1.4rem' }}>✨</span>
                                    <div style={{ flex: 1 }}>
                                        <strong style={{ color: '#004d40', fontSize: '0.95rem' }}>
                                            Horário sugerido: {suggestedTime.start} → {suggestedTime.end}
                                        </strong>
                                        <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#00695c' }}>
                                            Todos os participantes estão livres neste horário.
                                        </p>
                                    </div>
                                    <button onClick={handleApply} style={{
                                        background: 'linear-gradient(135deg, #00897b, #004d40)',
                                        color: '#fff', border: 'none', borderRadius: '8px',
                                        padding: '8px 18px', fontWeight: 600, fontSize: '0.85rem',
                                        cursor: 'pointer', whiteSpace: 'nowrap'
                                    }}>
                                        ✓ Usar este horário
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '16px 28px',
                    borderTop: '1px solid #e9ecef',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    gap: '12px', flexWrap: 'wrap'
                }}>
                    <button onClick={onClose} style={{
                        padding: '8px 20px', borderRadius: '8px',
                        border: '1px solid #dee2e6', background: '#f8f9fa',
                        color: '#495057', cursor: 'pointer', fontWeight: 500
                    }}>
                        Fechar
                    </button>

                    {!loading && someUnavailable && !suggestedTime && (
                        <button onClick={handleSuggest} disabled={suggesting} style={{
                            padding: '10px 22px', borderRadius: '8px',
                            background: suggesting ? '#6c757d' : 'linear-gradient(135deg, #ff6f00, #e65100)',
                            color: '#fff', border: 'none', fontWeight: 700,
                            fontSize: '0.9rem', cursor: suggesting ? 'not-allowed' : 'pointer',
                            boxShadow: '0 4px 12px rgba(230,81,0,0.3)',
                            display: 'flex', alignItems: 'center', gap: '8px'
                        }}>
                            {suggesting ? '⏳ Procurando...' : '✨ Sugerir Horário Comum'}
                        </button>
                    )}

                    {!loading && suggestedTime && (
                        <button onClick={handleApply} style={{
                            padding: '10px 22px', borderRadius: '8px',
                            background: 'linear-gradient(135deg, #00897b, #004d40)',
                            color: '#fff', border: 'none', fontWeight: 700,
                            fontSize: '0.9rem', cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(13,71,161,0.3)'
                        }}>
                            ✓ Aplicar {suggestedTime.start}→{suggestedTime.end} no Agendamento
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AvailabilityPanel;
